import React, { useEffect } from 'react';
import { useMarkets, MarketFilter } from '../hooks/useMarkets';
import BetBox from '../components/BetBox';
import MarketCycleStatus from '../components/MarketCycleStatus';
import TxToast from '../components/common/TxToast';
import { User } from '../types/auth';

interface MarketsPageProps {
  user?: User | null;
}

/**
 * MarketsPage
 *
 * Public/authenticated market listing page with:
 * - Filter tabs (all / active / expired / resolved)
 * - Search bar
 * - BetBox for each market when user is authenticated
 */
export default function MarketsPage({ user }: MarketsPageProps) {
  const {
    filteredMarkets,
    loadingMarkets,
    filter,
    searchQuery,
    setFilter,
    setSearchQuery,
    refetch,
    tx,
    resetTx,
    placeBet,
  } = useMarkets();

  useEffect(() => {
    refetch();
  }, [refetch]);

  const FILTER_OPTIONS: { value: MarketFilter; label: string }[] = [
    { value: 'all',      label: 'All'      },
    { value: 'active',   label: '🟢 Active'   },
    { value: 'expired',  label: '🔴 Expired'  },
    { value: 'resolved', label: '✅ Resolved' },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h2 className="page-title">📊 Prediction Markets</h2>
        <p className="page-subtitle">Browse and bet on active prediction markets.</p>
      </div>

      <TxToast tx={tx} onDismiss={resetTx} />

      {/* Controls */}
      <div className="markets-controls">
        <div className="filter-tabs">
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`filter-tab${filter === opt.value ? ' active' : ''}`}
              onClick={() => setFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <input
          type="search"
          className="form-input markets-search"
          placeholder="Search markets…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          aria-label="Search markets"
        />

        <button
          className="btn-outline"
          onClick={refetch}
          disabled={loadingMarkets}
        >
          {loadingMarkets ? '⟳' : '⟳ Refresh'}
        </button>
      </div>

      {/* Market list */}
      {loadingMarkets ? (
        <div className="loading-state">Loading markets…</div>
      ) : filteredMarkets.length === 0 ? (
        <div className="empty-state">
          {searchQuery
            ? `No markets match "${searchQuery}".`
            : 'No markets found.'}
        </div>
      ) : (
        <div className="markets-grid">
          {filteredMarkets.map(market => (
            <div key={market.id} className="market-card">
              <div className="market-card-header">
                <span className="market-id">#{market.id}</span>
                <MarketCycleStatus market={market} compact />
              </div>

              {user ? (
                <BetBox
                  marketId={market.id}
                  question={market.question}
                  yesPool={market.yesPool}
                  noPool={market.noPool}
                  deadline={market.deadline}
                  resolved={market.resolved}
                  onPlaceBet={placeBet}
                  txPending={tx.status === 'pending'}
                />
              ) : (
                <div className="market-card-public">
                  <p className="market-question">{market.question}</p>
                  <div className="market-pools">
                    <span>YES: {parseFloat(market.yesPool).toFixed(4)} ETH</span>
                    <span>NO: {parseFloat(market.noPool).toFixed(4)} ETH</span>
                  </div>
                  <p className="sign-in-prompt">Sign in to place a bet.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
